import { Suspense } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  Toolbar,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link, Outlet } from 'react-router-dom';
import { BrandMark } from '@/shared/ui/BrandMark';
import { TopBar } from './TopBar';
import { NavSection } from './NavSection';
import { SidebarPersonaCard } from './SidebarPersonaCard';
import { ThemeToggle } from './ThemeToggle';
import { WhatsNewRow } from './WhatsNewRow';
import { MaintenanceBanner } from './MaintenanceBanner';
import { useSidebarCollapsed } from './useSidebarCollapsed';
import { useMobileNavOpen } from './useMobileNavOpen';
import { useNewLeadsBadgeCount } from '@/modules/leads';
import { NAV_SECTIONS, LEADS_NAV_ITEM_ID } from './navConfig';
import type { NavItemBadge } from './NavSection';

const DRAWER_WIDTH_EXPANDED = 220;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Authenticated app shell: left nav + routed content outlet, with a
 * mobile-only top bar (`TopBar` renders `null` on desktop).
 *
 * Desktop (≥`md`): a permanent drawer that animates between a full 220px
 * (icon + label) and a 64px mini-variant (icon-only, label as hover
 * tooltip). Its own header row (brand mark + collapse toggle, below) has
 * replaced the old always-on shell `AppBar` — matching app.zip, where the
 * logo lives in the sidebar and there is no separate full-width bar above
 * it at all. State + persistence live in `useSidebarCollapsed` so the
 * toggle button and the Drawer's width share the same source of truth
 * without prop-drilling.
 *
 * Below `md` (~900px — the same shell breakpoint the design source uses):
 * the drawer becomes a temporary overlay, closed by default. Opening it is
 * the one job the mobile `TopBar` still does (wired to `useMobileNavOpen`),
 * and it is dismissed automatically on nav-item click or backdrop tap. The
 * drawer always renders full-width (icon + label) on mobile — an icon-only
 * rail makes sense for a permanent drawer reclaiming desktop width, not for
 * a full-screen overlay on a phone — so the collapse toggle in its header
 * row only renders when the drawer is the permanent (desktop) variant.
 *
 * Nav structure: 4 static (non-collapsible) labeled sections — Overview /
 * Records / Insights / Setup — defined in `navConfig.ts` and rendered by
 * `NavSection`, which also hides a section's label entirely when every one
 * of its items resolves to role-hidden (relevant today only to Setup/Admin,
 * but the rule is generic so it stays correct if another section gains a
 * role-gated item later).
 *
 * The persona/user-identity control lives at the bottom of the Drawer
 * (`SidebarPersonaCard`), pinned there via an explicit flex column layout:
 * the Drawer paper is a flex column, the scrollable nav sections are a
 * `flex: 1; overflowY: auto` region, and `WhatsNewRow` / `ThemeToggle` /
 * `SidebarPersonaCard` are separate flex siblings below, in that order —
 * deliberately OUTSIDE both that scrollable region and the
 * `<List onClick={isMobile ? mobileNav.close : undefined}>`
 * mobile-auto-close wrapper. Nesting any of them inside that List
 * would fire `mobileNav.close()` (unmounting the Drawer, and the Menu's
 * `anchorEl` with it) on every tap before its own popover/menu could
 * open — silently making "Sign out" (or What's New) unreachable on mobile.
 * `WhatsNewRow` owns a popover for the same reason `SidebarPersonaCard`
 * does, and follows its single-stable-JSX-branch pattern across
 * `collapsed`, not `ThemeToggle`'s simpler ternary — see `WhatsNewRow.tsx`.
 */
export const AppShell = (): JSX.Element => {
  const { collapsed, toggle: toggleCollapsed } = useSidebarCollapsed();
  const mobileNav = useMobileNavOpen();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const effectiveCollapsed = !isMobile && collapsed;
  const drawerWidth = effectiveCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  // Red badge on the Leads nav item showing the count of leads in
  // ``status='new'``. Errors / loading both fall back to 0 so the nav
  // never renders ``NaN`` and never crashes when the BE is offline.
  const { data: newLeadsCount } = useNewLeadsBadgeCount();
  const leadsBadge = newLeadsCount ?? 0;

  const getBadge = (itemId: string): NavItemBadge | undefined =>
    itemId === LEADS_NAV_ITEM_ID
      ? { content: leadsBadge, label: `${leadsBadge} new leads` }
      : undefined;

  const collapseToggleLabel = collapsed ? 'Expand navigation' : 'Collapse navigation';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <TopBar />
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileNav.open : true}
        onClose={isMobile ? mobileNav.close : undefined}
        ModalProps={isMobile ? { keepMounted: true } : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: (t) =>
              t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.shorter,
              }),
          },
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.shorter,
            }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: effectiveCollapsed ? 'center' : 'space-between',
            minHeight: 64,
            px: effectiveCollapsed ? 0 : 2,
          }}
        >
          {!effectiveCollapsed ? (
            <Link
              to="/dashboard"
              style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}
            >
              <BrandMark height={28} title="Qfly CRM" />
            </Link>
          ) : null}
          {/* The rail-collapse toggle only makes sense for the permanent
              (desktop) drawer — the mobile overlay is never a mini-rail, so
              there is nothing here to collapse. */}
          {!isMobile ? (
            <Tooltip title={collapseToggleLabel}>
              <IconButton
                onClick={toggleCollapsed}
                aria-label={collapseToggleLabel}
                aria-expanded={!collapsed}
                size="small"
              >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        <Divider />
        <Box
          data-testid="nav-scroll-region"
          sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
        >
          <List dense sx={{ px: 1 }} onClick={isMobile ? mobileNav.close : undefined}>
            {NAV_SECTIONS.map((section) => (
              <NavSection
                key={section.id}
                label={section.label}
                items={section.items}
                collapsed={effectiveCollapsed}
                getBadge={getBadge}
              />
            ))}
          </List>
        </Box>
        <Divider />
        <WhatsNewRow collapsed={effectiveCollapsed} />
        <ThemeToggle collapsed={effectiveCollapsed} />
        <SidebarPersonaCard collapsed={effectiveCollapsed} />
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // `minWidth: 0` is load-bearing (QFLY-407). As a flex item `main`
          // defaults to `min-width: auto`, i.e. the min-content width of
          // everything inside it, so one wide descendant — the client detail
          // tab strip laid flat at 816–896px — stopped `main` shrinking with the
          // viewport and the whole PAGE scrolled sideways instead (174px at 1024,
          // 210px at 768, 492px at 390 with the nav expanded). With a real
          // minimum, `main` is exactly the viewport minus the drawer, every
          // percentage-width child follows it, and in-page scrollers
          // (`Tabs variant="scrollable"`, table wrappers with `overflowX:
          // 'auto'`) finally have something narrower than their content to
          // scroll inside. Content that is genuinely wider than the viewport
          // and has no scroller still overflows exactly as before.
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          bgcolor: 'background.default',
        }}
      >
        {/* Spacer matching the fixed mobile TopBar's height — only needed
            when that bar actually renders. On desktop there is no fixed
            bar above `main` any more, so reserving room for one here would
            reintroduce exactly the wasted empty space this restructure
            removes from the Drawer side. */}
        {isMobile ? <Toolbar /> : null}
        <MaintenanceBanner />
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', height: 256, alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress aria-label="Loading page" />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      </Box>
    </Box>
  );
};
